import _ from "lodash"
import { useEffect, useMemo, useRef, useState } from "react"

type UseAsyncSearchOptions = {
  /** When true, skip internal refetch effects (search is driven elsewhere). */
  disabled?: boolean
}

export const useAsyncSearch = <T extends Record<string, any>>(
  asyncRefetch?: (searchQuery?: string) => Promise<T[]>,
  options: UseAsyncSearchOptions = {}
) => {
  const { disabled = false } = options
  const [searchValue, setSearchValue] = useState("")
  const inputRef = useRef<HTMLInputElement>(null)
  const asyncRefetchRef = useRef(asyncRefetch)
  asyncRefetchRef.current = asyncRefetch

  const debouncedSearch = useMemo(
    () =>
      _.debounce(async (query: string) => {
        const refetch = asyncRefetchRef.current
        if (refetch) {
          try {
            await refetch(query)
          } catch (error) {
            console.error(error)
          }
        }
      }, 100),
    []
  )

  useEffect(() => {
    if (disabled) {
      return
    }
    if (searchValue === "") {
      asyncRefetchRef.current?.("")
    }
    if (searchValue) {
      debouncedSearch(searchValue)
    }
    return () => {
      debouncedSearch.cancel()
    }
  }, [searchValue, debouncedSearch, disabled])

  return {
    searchValue,
    setSearchValue,
    inputRef,
  }
}
